/**
 * ShiftSummary Component
 * Displays current shift statistics and recent check-ins
 */

import React, { useState, useEffect } from 'react';
import checkInService from '../../../../services/checkInService';
import Icon from '../../../../components/AppIcon';

const ShiftSummary = ({ shift }) => {
  const [checkIns, setCheckIns] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (shift?.id) {
      fetchCheckIns();
    }
  }, [shift?.id]);

  // Subscribe to check-in updates
  useEffect(() => {
    if (!shift?.id) return;

    const subscription = checkInService.subscribeToCheckIns(shift.id, (payload) => {
      if (payload.eventType === 'INSERT') {
        setCheckIns(prev => [...prev, payload.new]);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [shift?.id]);

  const fetchCheckIns = async () => {
    const { data } = await checkInService.getCheckInsForShift(shift.id);
    if (data) {
      setCheckIns(data);
    }
  };

  if (!shift) return null;

  const startTime = new Date(shift.start_time);
  const totalHours = parseFloat(shift.total_hours) || 0;
  const totalCheckIns = checkIns.length;  // Count from fetched check-ins array

  // Calculate elapsed time
  const now = new Date();
  const elapsedMs = now - startTime;
  const elapsedHours = Math.floor(elapsedMs / (1000 * 60 * 60));
  const elapsedMinutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));

  // Format currency
  const hourlyRate = parseFloat(shift.contracts?.hourly_rate) || 0;
  const estimatedEarnings = totalHours * hourlyRate;

  return (
    <div className="bg-white rounded-xl overflow-hidden">
      {/* Summary Stats */}
      <div className="p-4 grid grid-cols-3 gap-4">
        {/* Hours Logged */}
        <div className="text-center">
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {totalHours.toFixed(1)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Hours Logged
          </div>
        </div>

        {/* Check-ins */}
        <div className="text-center border-x border-gray-200 dark:border-gray-600">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {totalCheckIns}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Check-ins
          </div>
        </div>

        {/* Earnings */}
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            ${estimatedEarnings.toFixed(2)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Estimated
          </div>
        </div>
      </div>

      {/* Shift Info */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <Icon name="Clock" className="h-4 w-4" />
            <span>Started at {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <div className="text-gray-500 dark:text-gray-400">
            {elapsedHours}h {elapsedMinutes}m elapsed
          </div>
        </div>
        
        {shift.contracts?.jobs?.title && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-2">
            <Icon name="Briefcase" className="h-4 w-4" />
            <span>{shift.contracts.jobs.title}</span>
          </div>
        )}
      </div>

      {/* Check-in Timeline Toggle */}
      {checkIns.length > 0 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Icon name="List" className="h-4 w-4" />
            View Check-in Timeline
          </span>
          <Icon 
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
            className="h-4 w-4" 
          />
        </button>
      )}

      {/* Check-in Timeline */}
      {isExpanded && checkIns.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-600 max-h-64 overflow-y-auto">
          <div className="space-y-3">
            {checkIns.slice().reverse().map((checkIn, index) => (
              <CheckInItem 
                key={checkIn.id} 
                checkIn={checkIn} 
                isLatest={index === 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Shift Status Indicator */}
      {shift.status === 'paused' && (
        <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400 text-sm">
            <Icon name="PauseCircle" className="h-4 w-4" />
            <span>Shift is paused - Resume to continue logging time</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual check-in item component
const CheckInItem = ({ checkIn, isLatest }) => {
  const checkInTime = new Date(checkIn.check_in_time);
  const isOnTime = checkIn.verification_status === 'on_time';
  
  return (
    <div className={`flex gap-3 ${isLatest ? 'opacity-100' : 'opacity-70'}`}>
      {/* Timeline indicator */}
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full ${
          isOnTime
            ? 'bg-green-500' 
            : 'bg-yellow-500'
        }`} />
        <div className="w-0.5 h-full bg-gray-200 dark:bg-gray-600 mt-1" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Check-in
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {checkInTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        
        {checkIn.tasks_description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
            {checkIn.tasks_description}
          </p>
        )}

        {/* Status badge */}
        <div className="flex items-center gap-2 mt-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
            isOnTime
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
          }`}>
            {isOnTime ? (
              <>
                <Icon name="Check" className="h-3 w-3" />
                On Time
              </>
            ) : (
              <>
                <Icon name="Clock" className="h-3 w-3" />
                Late
              </>
            )}
          </span>

          {checkIn.is_verified && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
              <Icon name="CheckCircle" className="h-3 w-3" />
              Verified
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftSummary;
